/**
 * Borrower Portal — blinkemi launch Track C (2026-07-03).
 *
 * Mobile-first self-service portal for Nishtha Leasing NBFC borrowers at
 * portal.blinkemi.com (also reachable at /portal/borrower on any tenant host).
 *
 * RBI Digital Lending Guidelines surface:
 *  - phone → OTP login (no password)
 *  - application status timeline (applied → KYC → decision → disbursed)
 *  - active loan card: outstanding, next EMI + due date, penal charges
 *  - full repayment schedule
 *  - KFS (Key Facts Statement) download
 *  - grievance form + list with status, Grievance Redressal Officer contact
 *    and RBI Sachet escalation note.
 *
 * Backend: /api/v1/portal/borrower/* (see borrower-portal.routes.ts).
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { formatINR, formatDate } from '../../utils/formatters';

const api = axios.create({ baseURL: '/api/v1/portal/borrower' });

const TOKEN_KEY = 'borrower_portal_token';

// paise (string | number) → ₹ display
function inr(paise: string | number | null | undefined, decimals = false): string {
  if (paise == null) return '—';
  const n = Number(paise);
  if (!isFinite(n)) return '—';
  return formatINR(n / 100, decimals);
}

interface NextEmi {
  sequenceNo: number;
  dueDate: string;
  amountPaise: string;
}

interface ScheduleLine {
  sequenceNo: number;
  installmentDate: string;
  principalAmountPaise: string;
  interestAmountPaise: string;
  totalPaymentPaise: string;
  balanceLoanAmountPaise: string;
  isPaid: boolean;
}

interface LoanCard {
  id: string;
  loanNumber: string;
  status: string;
  loanAmountPaise: string;
  disbursedAmountPaise: string;
  disbursementDate: string | null;
  rateOfInterest: string | number | null;
  daysPastDue: number;
  principalOutstandingPaise: string;
  penalAccruedPaise: string;
  nextEmi: NextEmi | null;
  schedule: ScheduleLine[];
}

interface ApplicationRow {
  id: string;
  applicationNumber: string;
  status: string;
  workflowState: string | null;
  applicantName: string | null;
  loanAmountPaise: string;
  repaymentPeriods: number | null;
  rejectionReason: string | null;
  approvedAt: string | null;
  createdAt: string;
}

interface KfsRow {
  id: string;
  kfsNumber: string;
  status: string;
  acknowledged: boolean;
  issuedAt: string | null;
  sanctionedAmountPaise: string;
  aprPctAnnual: string | number;
  emiAmountPaise: string;
  loanTenureMonths: number;
  hasPdf: boolean;
}

interface Grievance {
  id: string;
  number: string;
  title: string;
  status: string;
  createdAt: string;
}

interface Snapshot {
  applications: ApplicationRow[];
  loans: LoanCard[];
  kfs: KfsRow[];
}

// ---------------------------------------------------------------------------
// Application status timeline
// ---------------------------------------------------------------------------

const TIMELINE_STEPS = ['Applied', 'KYC & Review', 'Decision', 'Disbursed'] as const;

function timelineIndex(app: ApplicationRow, hasDisbursedLoan: boolean): number {
  const s = (app.status || '').toLowerCase();
  if (hasDisbursedLoan) return 3;
  if (s === 'approved' || s === 'sanctioned') return 2;
  if (s === 'rejected') return 2;
  if (s === 'under review' || s === 'kyc' || app.workflowState) return 1;
  return 0;
}

function StatusTimeline({ app, hasDisbursedLoan }: { app: ApplicationRow; hasDisbursedLoan: boolean }) {
  const idx = timelineIndex(app, hasDisbursedLoan);
  const rejected = (app.status || '').toLowerCase() === 'rejected';
  return (
    <div className="mt-3 flex items-center">
      {TIMELINE_STEPS.map((step, i) => {
        const done = i < idx || (i === idx && (i === 3 || rejected));
        const active = i === idx;
        const color = rejected && i >= 2
          ? 'bg-red-500 text-white'
          : done || active
            ? 'bg-emerald-600 text-white'
            : 'bg-gray-200 text-gray-500';
        return (
          <div key={step} className="flex flex-1 items-center last:flex-none">
            <div className="flex flex-col items-center">
              <div className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold ${color}`}>
                {i + 1}
              </div>
              <span className="mt-1 w-16 text-center text-[10px] leading-tight text-gray-600">
                {rejected && i === 2 ? 'Rejected' : step}
              </span>
            </div>
            {i < TIMELINE_STEPS.length - 1 && (
              <div className={`mx-1 mb-4 h-0.5 flex-1 ${i < idx ? 'bg-emerald-600' : 'bg-gray-200'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status chip
// ---------------------------------------------------------------------------

function StatusChip({ status }: { status: string }) {
  const s = (status || '').toLowerCase();
  const cls =
    s === 'open' || s === 'in_progress'
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : s === 'closed' || s === 'resolved' || s === 'approved' || s === 'disbursed'
        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
        : s === 'rejected'
          ? 'bg-red-50 text-red-700 border-red-200'
          : 'bg-gray-100 text-gray-600 border-gray-200';
  return (
    <span className={`inline-block rounded-full border px-2 py-0.5 text-xs font-medium capitalize ${cls}`}>
      {status?.replace(/_/g, ' ') || '—'}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Login (phone → OTP)
// ---------------------------------------------------------------------------

function LoginScreen({ onToken }: { onToken: (t: string) => void }) {
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [stage, setStage] = useState<'phone' | 'otp'>('phone');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);

  const requestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!/^\d{10}$/.test(phone)) {
      setError('Enter your 10-digit mobile number');
      return;
    }
    setBusy(true);
    try {
      const r = await api.post('/otp/request', { phone });
      setInfo(r.data?.message || 'OTP sent. Valid for 15 minutes.');
      setStage('otp');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not send OTP. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!/^\d{6}$/.test(otp)) {
      setError('Enter the 6-digit OTP');
      return;
    }
    setBusy(true);
    try {
      const r = await api.post('/otp/verify', { phone, otp });
      onToken(r.data.data.token);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Invalid or expired OTP');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-700 text-xl font-bold text-white">
            b
          </div>
          <h1 className="mt-4 text-2xl font-bold text-gray-900">Borrower Portal</h1>
          <p className="text-sm text-gray-500">
            blinkemi · Nishtha Leasing Pvt Ltd (RBI-registered NBFC)
          </p>
        </div>
        {stage === 'phone' ? (
          <form onSubmit={requestOtp} className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            {error && <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-600">{error}</div>}
            <label className="mb-1 block text-sm font-medium text-gray-700">Mobile number</label>
            <div className="flex">
              <span className="inline-flex items-center rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 px-3 text-sm text-gray-600">
                +91
              </span>
              <input
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                maxLength={10}
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="10-digit mobile"
                className="w-full rounded-r-lg border border-gray-300 px-3 py-2.5 text-base focus:border-emerald-600 focus:outline-none"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="mt-4 w-full rounded-lg bg-emerald-700 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
            >
              {busy ? 'Sending…' : 'Send OTP'}
            </button>
            <p className="mt-3 text-center text-xs text-gray-400">
              Use the mobile number from your loan application.
            </p>
          </form>
        ) : (
          <form onSubmit={verifyOtp} className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            {error && <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-600">{error}</div>}
            {info && <div className="mb-4 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-700">{info}</div>}
            <label className="mb-1 block text-sm font-medium text-gray-700">
              OTP sent to +91 {phone}
            </label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="6-digit OTP"
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-center text-lg tracking-[0.4em] focus:border-emerald-600 focus:outline-none"
            />
            <button
              type="submit"
              disabled={busy}
              className="mt-4 w-full rounded-lg bg-emerald-700 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
            >
              {busy ? 'Verifying…' : 'Verify & Sign in'}
            </button>
            <button
              type="button"
              onClick={() => { setStage('phone'); setOtp(''); setInfo(''); setError(''); }}
              className="mt-2 w-full py-2 text-center text-xs text-gray-500 underline"
            >
              Change number / resend OTP
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Grievance section
// ---------------------------------------------------------------------------

function GrievanceSection() {
  const [rows, setRows] = useState<Grievance[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(() => {
    api.get('/grievances').then((r) => setRows(r.data.data || [])).catch(() => setRows([]));
  }, []);

  useEffect(load, [load]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMsg('');
    setBusy(true);
    try {
      const r = await api.post('/grievances', { subject, description });
      setMsg(`Grievance ${r.data.data.number} registered. We will respond within the RBI-mandated timeline.`);
      setSubject('');
      setDescription('');
      setShowForm(false);
      load();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not submit grievance. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900">Grievances</h2>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="rounded-lg bg-emerald-700 px-3 py-1.5 text-xs font-semibold text-white"
        >
          {showForm ? 'Cancel' : 'Raise a grievance'}
        </button>
      </div>
      {msg && <div className="mt-3 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-700">{msg}</div>}
      {error && <div className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-600">{error}</div>}
      {showForm && (
        <form onSubmit={submit} className="mt-3 space-y-3">
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject (e.g. EMI debited twice)"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-emerald-600 focus:outline-none"
            maxLength={200}
            required
          />
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe your grievance in detail…"
            rows={4}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-emerald-600 focus:outline-none"
            maxLength={5000}
            required
          />
          <button
            type="submit"
            disabled={busy || subject.trim().length < 3 || description.trim().length < 10}
            className="w-full rounded-lg bg-emerald-700 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            {busy ? 'Submitting…' : 'Submit grievance'}
          </button>
        </form>
      )}
      <div className="mt-3 divide-y divide-gray-100">
        {rows.length === 0 && !showForm && (
          <p className="py-2 text-sm text-gray-400">No grievances raised yet.</p>
        )}
        {rows.map((g) => (
          <div key={g.id} className="flex items-center justify-between py-2.5">
            <div className="min-w-0 pr-3">
              <p className="truncate text-sm font-medium text-gray-800">{g.title}</p>
              <p className="text-xs text-gray-400">
                {g.number} · {formatDate(g.createdAt)}
              </p>
            </div>
            <StatusChip status={g.status} />
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function BorrowerPortalPage() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || '');
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [openSchedule, setOpenSchedule] = useState<string | null>(null);

  useEffect(() => {
    if (token) api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    else delete api.defaults.headers.common['Authorization'];
  }, [token]);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    api
      .get('/me')
      .then((r) => setSnapshot(r.data.data))
      .catch((err) => {
        if (err?.response?.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          setToken('');
        }
        setSnapshot(null);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const onToken = (t: string) => {
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setSnapshot(null);
  };

  const downloadKfs = (id: string) => {
    // Token travels as a query-less header — open via fetch → blob so the
    // Authorization header is attached (window.open drops it).
    api
      .get(`/kfs/${id}/download`, { responseType: 'blob' })
      .then((r) => {
        const url = URL.createObjectURL(r.data);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      })
      .catch(() => alert('Could not download KFS. Try again.'));
  };

  if (!token) return <LoginScreen onToken={onToken} />;

  const hasDisbursedLoan = (snapshot?.loans || []).some(
    (l) => (l.status || '').toLowerCase() === 'disbursed' || Number(l.disbursedAmountPaise) > 0,
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-10">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-gray-200 bg-white px-4 py-3">
        <div className="mx-auto flex max-w-2xl items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-700 text-sm font-bold text-white">
              b
            </div>
            <div>
              <p className="text-sm font-semibold leading-tight text-gray-900">blinkemi</p>
              <p className="text-[10px] leading-tight text-gray-400">Nishtha Leasing Pvt Ltd</p>
            </div>
          </div>
          <button onClick={logout} className="text-xs font-medium text-gray-500 underline">
            Sign out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-2xl space-y-4 px-4 pt-4">
        {loading && (
          <div className="flex justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-700 border-t-transparent" />
          </div>
        )}

        {!loading && snapshot && (
          <>
            {/* Applications */}
            {snapshot.applications.length === 0 && snapshot.loans.length === 0 && (
              <div className="rounded-xl border border-gray-200 bg-white p-6 text-center text-sm text-gray-500 shadow-sm">
                No loan applications found for this mobile number.
              </div>
            )}

            {snapshot.applications.map((app) => (
              <section key={app.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">
                      Application {app.applicationNumber}
                    </h2>
                    <p className="text-xs text-gray-400">
                      Applied {formatDate(app.createdAt)} · {inr(app.loanAmountPaise)}
                      {app.repaymentPeriods ? ` · ${app.repaymentPeriods} EMIs` : ''}
                    </p>
                  </div>
                  <StatusChip status={app.status} />
                </div>
                <StatusTimeline app={app} hasDisbursedLoan={hasDisbursedLoan} />
                {app.rejectionReason && (
                  <p className="mt-2 rounded-lg bg-red-50 p-2 text-xs text-red-600">
                    Reason: {app.rejectionReason}
                  </p>
                )}
              </section>
            ))}

            {/* Loans */}
            {snapshot.loans.map((loan) => (
              <section key={loan.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">Loan {loan.loanNumber}</h2>
                    <p className="text-xs text-gray-400">
                      {loan.disbursementDate ? `Disbursed ${formatDate(loan.disbursementDate)}` : 'Not disbursed yet'}
                    </p>
                  </div>
                  <StatusChip status={loan.status} />
                </div>

                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div className="rounded-lg bg-gray-50 p-3">
                    <p className="text-[11px] text-gray-500">Outstanding principal</p>
                    <p className="text-lg font-bold text-gray-900">{inr(loan.principalOutstandingPaise)}</p>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-3">
                    <p className="text-[11px] text-gray-500">Next EMI</p>
                    {loan.nextEmi ? (
                      <>
                        <p className="text-lg font-bold text-gray-900">{inr(loan.nextEmi.amountPaise)}</p>
                        <p className="text-[11px] text-gray-500">due {formatDate(loan.nextEmi.dueDate)}</p>
                      </>
                    ) : (
                      <p className="text-lg font-bold text-emerald-700">All paid</p>
                    )}
                  </div>
                </div>

                {(Number(loan.penalAccruedPaise) > 0 || loan.daysPastDue > 0) && (
                  <div className="mt-2 rounded-lg bg-red-50 p-3 text-xs text-red-700">
                    {loan.daysPastDue > 0 && <span>{loan.daysPastDue} day(s) past due. </span>}
                    {Number(loan.penalAccruedPaise) > 0 && (
                      <span>Penal charges accrued: {inr(loan.penalAccruedPaise, true)}.</span>
                    )}
                  </div>
                )}

                {loan.schedule.length > 0 && (
                  <div className="mt-3">
                    <button
                      onClick={() => setOpenSchedule(openSchedule === loan.id ? null : loan.id)}
                      className="text-xs font-semibold text-emerald-700 underline"
                    >
                      {openSchedule === loan.id ? 'Hide repayment schedule' : `View repayment schedule (${loan.schedule.length} instalments)`}
                    </button>
                    {openSchedule === loan.id && (
                      <div className="mt-2 overflow-x-auto rounded-lg border border-gray-100">
                        <table className="w-full min-w-[420px] text-left text-xs">
                          <thead className="bg-gray-50 text-gray-500">
                            <tr>
                              <th className="px-2 py-2">#</th>
                              <th className="px-2 py-2">Due date</th>
                              <th className="px-2 py-2 text-right">Principal</th>
                              <th className="px-2 py-2 text-right">Interest</th>
                              <th className="px-2 py-2 text-right">EMI</th>
                              <th className="px-2 py-2 text-center">Paid</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {loan.schedule.map((l) => (
                              <tr key={l.sequenceNo} className={l.isPaid ? 'text-gray-400' : 'text-gray-800'}>
                                <td className="px-2 py-1.5">{l.sequenceNo}</td>
                                <td className="px-2 py-1.5">{formatDate(l.installmentDate)}</td>
                                <td className="px-2 py-1.5 text-right">{inr(l.principalAmountPaise)}</td>
                                <td className="px-2 py-1.5 text-right">{inr(l.interestAmountPaise)}</td>
                                <td className="px-2 py-1.5 text-right font-medium">{inr(l.totalPaymentPaise)}</td>
                                <td className="px-2 py-1.5 text-center">{l.isPaid ? '✓' : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </section>
            ))}

            {/* KFS */}
            {snapshot.kfs.length > 0 && (
              <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <h2 className="text-base font-semibold text-gray-900">Key Facts Statement (KFS)</h2>
                <p className="mt-0.5 text-xs text-gray-400">
                  RBI-mandated summary of your loan's cost — keep it for your records.
                </p>
                <div className="mt-2 divide-y divide-gray-100">
                  {snapshot.kfs.map((k) => (
                    <div key={k.id} className="flex items-center justify-between py-2.5">
                      <div className="min-w-0 pr-3">
                        <p className="text-sm font-medium text-gray-800">{k.kfsNumber}</p>
                        <p className="text-xs text-gray-400">
                          {inr(k.sanctionedAmountPaise)} · APR {String(k.aprPctAnnual)}% · {k.loanTenureMonths} month(s)
                          {k.issuedAt ? ` · issued ${formatDate(k.issuedAt)}` : ''}
                        </p>
                      </div>
                      <button
                        onClick={() => downloadKfs(k.id)}
                        className="shrink-0 rounded-lg border border-emerald-700 px-3 py-1.5 text-xs font-semibold text-emerald-700"
                      >
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Grievances */}
            <GrievanceSection />

            {/* RBI footer */}
            <footer className="rounded-xl border border-gray-200 bg-white p-4 text-xs leading-relaxed text-gray-500 shadow-sm">
              <p className="font-semibold text-gray-700">Grievance Redressal Officer</p>
              <p>
                Nishtha Leasing Pvt Ltd (RBI CoR 14.01047) · Model Town, Panipat, Haryana
                <br />
                Email: grievance@blinkemi.com · Use the "Raise a grievance" option above for fastest resolution.
              </p>
              <p className="mt-2">
                If your grievance is not resolved within 30 days, you may escalate to the Reserve Bank of India
                under the RB-IOS Integrated Ombudsman Scheme at{' '}
                <a
                  href="https://sachet.rbi.org.in"
                  target="_blank"
                  rel="noreferrer"
                  className="text-emerald-700 underline"
                >
                  sachet.rbi.org.in
                </a>{' '}
                or cms.rbi.org.in.
              </p>
            </footer>
          </>
        )}
      </main>
    </div>
  );
}
